import { computed, ref } from 'vue';

import { setUserSettings } from '@/composables/useUserSettings';
import { logAppError } from '@/config/sentryConfig';
import { OWN_PROFILE_SETTINGS_PATH } from '@/services/userProfile/constants';
import { type User } from '@/types/api/user';
import { type UserSettings } from '@/types/api/user-settings';

interface UserSettingsPatchPayload {
  settingsKey: string;
  settingsValue: string | null;
}

interface UserSettingsPutPayload {
  dateFormat: string | null;
  lengthFormat: string;
  numberFormat: string;
  timeFormat: string | null;
  weightFormat: string;
}

// Module-level singleton state: the current user's raw profile.
const profile = ref<User | null>(null);

/**
 * Replace the current user profile (called from useAuth on app init, and on
 * logout with `null` to clear).
 */
export const setUserProfile = (user: User | null) => {
  profile.value = user;
};

/**
 * Typed access to the current user's profile.
 *
 * Replaces the former `$userProfile` global property and the `UserProfile`
 * class. Exposes the raw `profile` plus reactive derived fields and the own
 * profile settings mutations.
 */
export function useUserProfile() {
  const firstname = computed(() => profile.value?.firstName);
  const lastname = computed(() => profile.value?.lastName);
  const email = computed(() => profile.value?.email);
  const permissions = computed(() => profile.value?.permissions);
  const settings = computed(() => profile.value?.settings);
  const actions = computed(() => profile.value?.actions);
  const tenants = computed(() => profile.value?.tenants);

  const initials = computed(() => {
    const first = firstname.value;
    if (!first) return '';
    const second = lastname.value ?? '';
    return `${first.slice(0, 1).toUpperCase()}${second
      .slice(0, 1)
      .toUpperCase()}`;
  });

  /**
   * Persist the latest settings on the profile and refresh the derived
   * format settings. Both profile.value.settings (part of the User object)
   * and useUserSettings' singleton must be kept in sync whenever settings
   * change; if only one is updated, format computations will diverge.
   */
  const commitSettings = (responseBody: { userSettings: UserSettings }) => {
    if (profile.value) {
      profile.value.settings = responseBody.userSettings;
    }
    setUserSettings(responseBody.userSettings);
  };

  const putUserSettings = async (
    putPayload: UserSettingsPutPayload
  ): Promise<void> => {
    try {
      // Lazy import to avoid a module-load import cycle
      // (services/api → logoutService → useUserProfile → services/api).
      const { default: apiClient } = await import('@/services/api');
      const response = await apiClient.put(
        OWN_PROFILE_SETTINGS_PATH,
        putPayload
      );
      commitSettings(response.data);
    } catch (error) {
      logAppError({
        error,
        functionName: 'putUserSettings',
        section: 'userProfile'
      });
      throw error;
    }
  };

  const patchUserSetting = async (
    patchPayload: UserSettingsPatchPayload
  ): Promise<void> => {
    try {
      // Lazy import to avoid a module-load import cycle
      // (services/api → logoutService → useUserProfile → services/api).
      const { default: apiClient } = await import('@/services/api');
      const { data } = await apiClient.patch(
        OWN_PROFILE_SETTINGS_PATH,
        patchPayload
      );
      commitSettings(data);
    } catch (error) {
      logAppError({
        error,
        functionName: 'patchUserSetting',
        section: 'userProfile'
      });
      throw error;
    }
  };

  return {
    profile,
    firstname,
    lastname,
    initials,
    email,
    permissions,
    settings,
    actions,
    tenants,
    putUserSettings,
    patchUserSetting
  };
}

export default useUserProfile;
